import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useOverlay } from '@/lib/useOverlay'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'

type Tone = 'light' | 'dark'

/** Header, body and footer share one centred column, so on a tablet they line up. */
const COLUMN = 'mx-auto flex w-full max-w-xl items-center gap-3'

const TONES: Record<Tone, { panel: string; bar: string; close: string; sub: string }> = {
  // Frosted, not opaque: the page behind stays faintly visible, so the sheet reads as a layer
  // over the screen you were on rather than a different screen.
  light: {
    // 75%: glassier, so the screen behind stays present. Legible because the panel also blurs
    // what is behind it (backdrop-blur-2xl); where an engine can't blur, Reduce Transparency
    // users still get a solid panel (frost-solid).
    panel: 'frost-solid bg-white/75 text-neutral-900 dark:bg-neutral-900/75 dark:text-neutral-100',
    bar: 'border-black/5 bg-white/60 dark:border-white/10 dark:bg-neutral-900/60',
    close: 'text-neutral-600 hover:bg-black/5 dark:text-neutral-300 dark:hover:bg-white/10',
    sub: 'text-neutral-500 dark:text-neutral-400',
  },
  dark: {
    panel: 'frost-solid-dark bg-neutral-900/75 text-neutral-100',
    bar: 'border-white/10 bg-neutral-900/60',
    close: 'text-neutral-300 hover:bg-white/10',
    sub: 'text-neutral-400',
  },
}

/**
 * The phone/tablet presentation of a page's filter panel: full width, full height, frosted,
 * and always closable — ✕ in the header, Escape, the Android back gesture, or the primary
 * button in the footer. Desktop keeps each page's floating rail; this is only rendered below
 * the `lg` breakpoint, where a 288px rail would cover most of the screen anyway.
 *
 * Rendered through a portal so no page's stacking context (the full-bleed grid, the map's
 * overlay layer) can put it underneath something.
 */
export function FilterSheet({
  open, onClose, title, subtitle, tone = 'light', onReset, resetDisabled, doneLabel, children,
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  tone?: Tone
  /** Clears every filter in the sheet. Omit to hide the Reset button. */
  onReset?: () => void
  resetDisabled?: boolean
  doneLabel?: string
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const tc = useT(commonMessages)
  useOverlay(open, onClose, panel)
  if (!open) return null
  const t = TONES[tone]

  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className={`sheet-up fixed inset-0 z-[60] flex flex-col backdrop-blur-2xl backdrop-saturate-150 ${t.panel}`}
    >
      <header
        className={`shrink-0 border-b px-4 pb-3 ${t.bar}`}
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className={COLUMN}>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-lg font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className={`truncate text-xs ${t.sub}`}>{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tc('closeNamed', { name: title.toLowerCase() })}
            className={`grid size-11 shrink-0 place-items-center rounded-full transition ${t.close}`}
          >
            <CloseIcon />
          </button>
        </div>
      </header>

      {/* Phone: edge to edge. Tablet portrait: a centred column, not 768px-wide fields. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto w-full max-w-xl">{children}</div>
      </div>

      <footer
        className={`shrink-0 border-t px-4 pt-3 ${t.bar}`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className={COLUMN}>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              disabled={resetDisabled}
              className={`h-12 rounded-xl px-4 text-sm font-semibold transition disabled:opacity-40 ${t.close}`}
            >
              {tc('reset')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-12 flex-1 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 active:scale-[0.99]"
          >
            {doneLabel ?? tc('showResults')}
          </button>
        </div>
      </footer>
    </div>,
    document.body,
  )
}

/**
 * The control that opens a filter sheet. Carries a count of active filters, so a phone user
 * looking at a short list can tell it is short because of a filter, not because there is no data.
 */
export function FilterButton({
  onClick, count = 0, label, tone = 'light', className = '',
}: { onClick: () => void; count?: number; label?: string; tone?: Tone; className?: string }) {
  const tc = useT(commonMessages)
  const text = label ?? tc('filters')
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={count > 0 ? (label ? `${label}, ${count}` : tc('filtersActive', { count })) : text}
      className={`inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold shadow-lg ring-1 backdrop-blur-md transition active:scale-[0.98] ${
        tone === 'dark'
          ? 'bg-neutral-900/85 text-white ring-white/10 hover:bg-neutral-900'
          : 'bg-white/85 text-neutral-900 ring-black/5 hover:bg-white dark:bg-neutral-900/85 dark:text-neutral-100 dark:ring-white/10'
      } ${className}`}
    >
      <FilterIcon />
      {text}
      {count > 0 && (
        <span className="grid min-w-5 place-items-center rounded-full bg-brand-600 px-1.5 text-[11px] font-bold leading-5 text-white">
          {count}
        </span>
      )}
    </button>
  )
}

export function CloseIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  )
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round" />
    </svg>
  )
}
